import { useEffect, useState } from 'react';
import { Button, PageHeader, Textfield, PasswordConditions, PasswordValidation, validatePassword, isPasswordValid } from '@flexo/atoms';
import styles from './register.module.scss';
import { useTranslation } from 'react-i18next';
import { Browser } from '@capacitor/browser';
import { FirebaseService, httpService, IconWrapper } from '@flexo/general';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { MODULES } from '@flexo/reducers';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const ENV = (import.meta as any).env;
const PRIVACY_POLICY_URL = ENV.VITE_PRIVACY_POLICY_URL;
const TERMS_URL = ENV.VITE_TERMS_CONDITIONS_URL;

export enum RegisterStage {
  EnterEmailAndCode = 1,
  SetPassword = 2,
}

export function RegisterOOBcode(props) {
  const {
    Register__Header,
    Register__Header__Title,
    Register__Header__Subtitle,
    Register__Inputs,
    Register__Footer,
    Register__Footer__Register,
    Register__Footer__Register__Button,
    Register__Inputs__PasswordConditions,
    Register__Footer__Submit,
    Register__Privacy,
    Register__Privacy__Checkbox,
    Register__Privacy__Copy,
  } = styles;
  const { containedLabel, textfieldColor } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const PRIVACY_AND_TERMS = t('pages.register.privacy_and_terms', {
    returnObjects: true,
  });
  const navigate = useNavigate();

  const [currentStage, setCurrentStage] = useState<RegisterStage>(
    RegisterStage.EnterEmailAndCode
  );
  const [registerData, setRegisterData] = useState({
    email: '',
    OOBcode: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({
    email: false,
    OOBcode: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    lengthValid: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasUpperCase: false,
    hasLowerCase: false,
    passwordsMatch: false,
  });
  const [inputType, setInputType] = useState({
    newPassword: 'password',
    confirmPassword: 'password',
  });

  const [icon, setIcon] = useState({
    newPassword: 'visibility',
    confirmPassword: 'visibility',
  });
  const [passwordBlur, setPasswordBlur] = useState(false);
  const [confirmPasswordBlur, setConfirmPasswordBlur] = useState(false);
  const [confirmPasswordActive, setConfirmPasswordActive] = useState(false);

  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const isAndroid = Capacitor.getPlatform() === 'android';

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;
    setRegisterData({ ...registerData, [name]: value });
    if (name === 'newPassword') {
      handlePasswordValidation(value);
    }
    if (name === 'confirmPassword') {
      setPasswordValidation((prev) => ({
        ...prev,
        passwordsMatch: registerData.newPassword === value,
      }));
    }
  };

  const isEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  const handlePasswordValidation = (password: string) => {
    const validation = validatePassword(password, registerData.confirmPassword);
    setPasswordValidation(validation);
  };
  const togglePasswordVisibility = (name: string) => {
    setInputType((prevState) => ({
      ...prevState,
      [name]: prevState[name] === 'password' ? 'text' : 'password',
    }));
    setIcon((prevIcon) => ({
      ...prevIcon,
      [name]:
        prevIcon[name] === 'visibility' ? 'hide-visibility' : 'visibility',
    }));
  };

  const onNext = () => {
    if (currentStage === RegisterStage.EnterEmailAndCode) {
      setCurrentStage(RegisterStage.SetPassword);
    }
  };

  const completeRegistration = async () => {
    const firebaseService = new FirebaseService();

    const idToken = await firebaseService.getIdToken();
    try {
      const response = await httpService.post({
        url: `${ENV.VITE_APP_BASE_URL}/auth/v1_1/register`,
        data: {
          email: registerData.email,
          activation_code: registerData.OOBcode,
          password: registerData.newPassword,
          tenant_id: ENV.VITE_APP_TENANT,
        },
        idToken: idToken,
      });

      if (response.status === 200) {
        dispatch(MODULES.ActivateLoginModule());
      } else {
        // console.error('Failed to register:', response.data);
      }
    } catch (error) {
      // console.error('Error registering:', error);
    }
  };

  const handleBlur = (event: any) => {
    const { name, value } = event.target;
    if (name === 'newPassword') setPasswordBlur(true);

    if (name === 'email') {
      !isEmail(value)
        ? setErrors({ ...errors, email: true })
        : setErrors({ ...errors, email: false });
      setErrors({ ...errors, email: !isEmail(value) });
    }
    if (name === 'confirmPassword') {
      setConfirmPasswordBlur(true);
    }
  };

  const handleFocus = (event: any) => {
    const { name } = event.target;

    if (name === 'email') {
      setErrors({ ...errors, email: false });
    }

    if (name === 'newPassword') {
      setPasswordBlur(false);
    }
    if (name === 'confirmPassword') {
      setConfirmPasswordBlur(false);
      setConfirmPasswordActive(true);
    }
  };

  const openLink = async (url: string) => {
    Browser.open({ url });
  };



  useEffect(() => {
    // Keyboard opens
    Keyboard.addListener('keyboardWillShow', () => {
      setIsKeyboardOpen(true);
    });

    // Keyboard closes
    Keyboard.addListener('keyboardWillHide', () => {
      setIsKeyboardOpen(false);
    });

    return () => {
      try {
        Keyboard.removeAllListeners();
      } catch (error) {
        console.log(error);
      }
    };
  }, []);

  // Add direct DOM manipulation for Android
  useEffect(() => {
    if (isAndroid) {
      // Use setTimeout to ensure DOM is fully rendered
      const timeout = setTimeout(() => {
        const footerElements = document.querySelectorAll(
          `.${Register__Footer}`
        );
        footerElements.forEach((el) => {
          if (el) {
            (el as HTMLElement).style.position = 'absolute';
            (el as HTMLElement).style.bottom = '93px';
            (el as HTMLElement).style.zIndex = '9999';
            (el as HTMLElement).style.width = '90%';
          }
        });
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [isAndroid, Register__Footer]);

  return (
    <div className={styles.Register}>
      {isAndroid && (
        <style>
          {`
            .android-footer-fix {
              position: absolute !important;
              bottom: 50px !important;
              z-index: 9999 !important;
              width: 90% !important;
            }
          `}
        </style>
      )}
      <div className={styles.Register__Main}>
        <div className={Register__Header}>
          <PageHeader
            title={
              currentStage === 1
                ? t('pages.register.title')
                : t('pages.account_creation.title')
            }
            subtitle={
              currentStage === 1
                ? t('pages.register.subtitle')
                : t('pages.account_creation.subtitle')
            }
            back={
              currentStage === RegisterStage.SetPassword
                ? (t('pages.account_creation.get_back') as any)
                : undefined
            }
            backAction={() => {
              setCurrentStage(RegisterStage.EnterEmailAndCode);
            }}
          />
        </div>
        <div className={styles.Register__Content}>
          {currentStage === RegisterStage.EnterEmailAndCode && (
            <>
              <div className={Register__Inputs}>
                <div className={styles.Register_Inputs_Input}>
                  <Textfield
                    variant={containedLabel ? "containedLabel" : "default"}
                    textfieldColor={textfieldColor || 'white'}
                    name="email"  
                    label={t('pages.register.user_email') as string}
                    value={registerData.email}
                    placeholder={
                      t('pages.register.user_email_placeholder') as string
                    }
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    type="email"
                    error={errors.email}
                    containedLabel={containedLabel}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck="false"
                    description={
                      t('pages.register.user_email_description') as string
                    }
                    errorDescription={
                      errors.email ? 'Please enter a valid email address' : ''
                    }
                    onFocus={handleFocus}
                  />
                </div>
                <div className={styles.Register_Inputs_Input}>
                  <Textfield
                    variant={containedLabel ? "containedLabel" : "default"}
                    textfieldColor={textfieldColor || 'white'}
                    name="OOBcode"
                    label={t('pages.register.oob_code') as string}
                    value={registerData.OOBcode}
                    placeholder={
                      t('pages.register.oob_code_placeholder') as string
                    }
                    onChange={handleInputChange}
                    containedLabel={containedLabel}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck="false"
                    description={
                      t('pages.register.oob_code_description') as string
                    }
                  />
                </div>
              </div>
              <div className={Register__Privacy}>
                <input
                  type="checkbox"
                  onChange={() => setTermsAccepted(!termsAccepted)}
                  className={Register__Privacy__Checkbox}
                  checked={termsAccepted}
                />
                <span className={`${styles.Register__Privacy__Copy} detail`}>
                  {PRIVACY_AND_TERMS[0]}{' '}
                  <span
                    className={'color-primary-500'}
                    onClick={() => openLink(TERMS_URL)}
                  >
                    {PRIVACY_AND_TERMS[1]}
                  </span>{' '}
                  {PRIVACY_AND_TERMS[2]}{' '}
                  <span
                    className={'color-primary-500'}
                    onClick={() => openLink(PRIVACY_POLICY_URL)}
                  >
                    {PRIVACY_AND_TERMS[3]}
                  </span>
                </span>
              </div>
            </>
          )}

          {currentStage === RegisterStage.SetPassword && (
            <div className={Register__Inputs}>
              <div className={styles.Register_Inputs_Input}>
                <Textfield
                  variant={containedLabel ? "containedLabel" : "default"}
                  textfieldColor={textfieldColor || 'white'}
                  name="newPassword"
                  label={t('pages.register.password') as string}
                  value={registerData.newPassword}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  type={inputType.newPassword} // Updated to use inputType state
                  containedLabel={containedLabel}
                  icon={icon.newPassword} // Add icon for visibility toggle
                  onIconClick={() => togglePasswordVisibility('newPassword')} // Toggle visibility
                  error={errors.newPassword}
                  errorDescription={
                    errors.newPassword
                      ? 'Please review the password criteria'
                      : ''
                  }
                />
              </div>
              <PasswordConditions
                validation={passwordValidation}
                isFocused={!passwordBlur}
                conditionClassName={styles.Register__Conditions}
              />{' '}
              <div className={styles.Register_Inputs_Input}>
                <Textfield
                  variant={containedLabel ? "containedLabel" : "default"}
                  textfieldColor={textfieldColor || 'white'}
                  name="confirmPassword"  
                  label={t('pages.register.confirm_password') as string}
                  value={registerData.confirmPassword}
                  placeholder={
                    t('pages.register.confirm_password_placeholder') as string
                  }
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  type={inputType.confirmPassword} // Updated to use inputType state
                  containedLabel={containedLabel}
                  icon={icon.confirmPassword} // Icon for visibility toggle
                  onIconClick={() =>
                    togglePasswordVisibility('confirmPassword')
                  } // Toggle visibility
                  error={
                    !passwordValidation.passwordsMatch &&
                    confirmPasswordBlur &&
                    registerData.confirmPassword.length > 0
                  }
                  errorDescription={
                    !passwordValidation.passwordsMatch &&
                    confirmPasswordBlur &&
                    registerData.confirmPassword.length > 0
                      ? 'Passwords do not match'
                      : ''
                  }
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className={`${Register__Footer} ${
          isAndroid ? 'android-footer-fix' : ''
        }`}
        style={{
          display: isKeyboardOpen ? 'none' : 'flex',
        }}
      >
        <Button
          size='large'
          onClick={
            currentStage === RegisterStage.EnterEmailAndCode
              ? onNext
              : completeRegistration
          }
          label={
            currentStage === RegisterStage.EnterEmailAndCode
              ? t('pages.register.next')
              : t('pages.register.submit')
          }
          variant="primary"
          color="secondary"
          disabled={
            (currentStage === RegisterStage.EnterEmailAndCode &&
              (!registerData.email ||
                !registerData.OOBcode ||
                !termsAccepted ||
                !isEmail(registerData.email))) ||
            (currentStage === RegisterStage.SetPassword &&
              (!registerData.newPassword ||
                !registerData.confirmPassword ||
                !isPasswordValid(registerData.newPassword) ||
                !passwordValidation.passwordsMatch))
          }
        />

        <div
          className={` paragraph ${Register__Footer__Register}`}
          style={{ minHeight: '24px' }}
        >
          {currentStage === RegisterStage.EnterEmailAndCode ? (
            <div>
              {t('pages.register.already_registered')}{' '}
              <button
                className={` paragraph ${Register__Footer__Register__Button}`}
                onClick={() => navigate(-1)}
              >
                {t('pages.register.go_to_login')}
              </button>
            </div>
          ) : (
            <div aria-hidden="true"></div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RegisterOOBcode;
